import React from 'react';
import { User } from 'lucide-react';

interface UserBreakdownRow {
  user_name: string;
  user_id?: number | null;
  count: number;
  [key: string]: any;
}

interface UserBreakdownTableProps {
  title: string;
  data: UserBreakdownRow[];
  columns?: Array<{
    key: string;
    label: string;
    render?: (value: any, row: UserBreakdownRow) => React.ReactNode;
  }>;
  showTotal?: boolean;
  emptyMessage?: string;
}

export const UserBreakdownTable: React.FC<UserBreakdownTableProps> = ({
  title,
  data,
  columns = [{ key: 'count', label: 'Count' }],
  showTotal = true,
  emptyMessage = 'No data for selected period'
}) => {
  const total = data.reduce((sum, row) => sum + row.count, 0);

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
        <div className="p-6 text-center text-gray-500">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, index) => (
              <tr key={row.user_id || index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-gray-500" />
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {row.user_name}
                      </div>
                    </div>
                  </div>
                </td>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900"
                  >
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {showTotal && (
            <tfoot className="bg-gray-50">
              <tr>
                <td className="px-6 py-3 whitespace-nowrap">
                  <span className="text-sm font-semibold text-gray-900">Total</span>
                </td>
                {columns.map((col, colIndex) => (
                  <td
                    key={col.key}
                    className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900"
                  >
                    {colIndex === 0 ? total : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

interface ProductBreakdownRow {
  product_name: string;
  product_id?: number | null;
  count: number;
  [key: string]: any;
}

interface ProductBreakdownTableProps {
  title: string;
  data: ProductBreakdownRow[];
  columns?: Array<{
    key: string;
    label: string;
    render?: (value: any, row: ProductBreakdownRow) => React.ReactNode;
  }>;
  showTotal?: boolean;
  emptyMessage?: string;
}

export const ProductBreakdownTable: React.FC<ProductBreakdownTableProps> = ({
  title,
  data,
  columns = [{ key: 'count', label: 'Count' }],
  showTotal = true,
  emptyMessage = 'No data for selected period'
}) => {
  const total = data.reduce((sum, row) => sum + row.count, 0);

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        </div>
        <div className="p-6 text-center text-gray-500">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, index) => (
              <tr key={row.product_id || index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {row.product_name}
                  </div>
                </td>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900"
                  >
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {showTotal && (
            <tfoot className="bg-gray-50">
              <tr>
                <td className="px-6 py-3 whitespace-nowrap">
                  <span className="text-sm font-semibold text-gray-900">Total</span>
                </td>
                {columns.map((col, colIndex) => (
                  <td
                    key={col.key}
                    className="px-6 py-3 whitespace-nowrap text-right text-sm font-semibold text-gray-900"
                  >
                    {colIndex === 0 ? total : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default UserBreakdownTable;

